/**
 * Détermine quelles étapes d'une séance comptent réellement pour la complétion.
 *
 * Certaines lignes « techniques » (résumé d'un workout structuré type AMRAP/EMOM
 * et instruction libre) sont persistées comme étapes mais affichées en entête de
 * bloc côté client, jamais dans la liste à valider. Si on les comptait dans le
 * calcul de complétion, valider tous les exercices visibles d'un bloc ne suffirait
 * pas à passer la séance en « terminée » (elle resterait « partielle »).
 *
 * Cette fonction est le miroir backend de `selectVisibleSteps` (ProgramDayBlocks),
 * afin que le statut de séance calculé côté API corresponde à ce que voit le client.
 */
import {
  buildCustomWorkoutSummaryLine,
  parseBlockGroupMeta,
} from "./block-meta";
import { groupStepsByContiguousGroupTitle } from "./program-steps";

export type CompletableStepInput = {
  title?: string | null;
  position?: number | null;
  groupId?: string | null;
  groupTitle?: string | null;
  groupDescription?: string | null;
  exerciseId?: string | null;
};

export const selectCompletableStepsInBlock = <T extends CompletableStepInput>(
  steps: T[],
  summaryLine: string | null,
  instruction: string | null,
): T[] => {
  const titleOf = (step: T): string => step.title?.trim() ?? "";
  const isMeta = (step: T): boolean => !step.exerciseId;

  const visible = [...steps];

  if (summaryLine) {
    const idx = visible.findIndex(
      (step) => isMeta(step) && titleOf(step) === summaryLine,
    );
    if (idx !== -1) visible.splice(idx, 1);
  }

  if (instruction) {
    const instructionLines = instruction
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);
    for (let i = instructionLines.length - 1; i >= 0; i -= 1) {
      const last = visible.at(-1);
      if (last && isMeta(last) && titleOf(last) === instructionLines[i]) {
        visible.pop();
      } else {
        break;
      }
    }
  }

  // Un bloc composé uniquement de lignes techniques reste comptabilisé tel quel.
  return visible.length > 0 ? visible : steps;
};

/**
 * Filtre les étapes d'une séance pour ne garder que celles qui comptent dans le
 * calcul de complétion (exclut résumé workout + instruction libre).
 */
export const selectCompletableSessionSteps = <T extends CompletableStepInput>(
  steps: readonly T[],
): T[] => {
  const ordered = [...steps].sort(
    (a, b) => (a.position ?? 0) - (b.position ?? 0),
  );
  const groups = groupStepsByContiguousGroupTitle(ordered);
  const result: T[] = [];
  for (const group of groups) {
    const head = group.items[0];
    const meta = parseBlockGroupMeta(head?.groupDescription);
    const summaryLine = meta.workoutType
      ? buildCustomWorkoutSummaryLine(meta.workoutType, meta.workoutParams ?? {})
      : null;
    result.push(
      ...selectCompletableStepsInBlock(group.items, summaryLine, meta.instruction),
    );
  }
  return result;
};

type CompletableStepWithId = CompletableStepInput & {
  id: string;
  type?: "CHECKBOX" | "NUMBER" | "DURATION" | "TEXT";
  isDone?: boolean;
  valueNumber?: number | null;
  valueText?: string | null;
  targetValue?: number | null;
};

/**
 * Identifie les lignes techniques d'un bloc dont toutes les étapes comptables sont
 * terminées — à marquer automatiquement comme faites (miroir de « Valider le bloc »).
 */
export const technicalStepIdsWhenBlocksCompletable = <
  T extends CompletableStepWithId,
>(
  steps: readonly T[],
  isStepCompleted: (step: T) => boolean,
): string[] => {
  const ordered = [...steps].sort(
    (a, b) => (a.position ?? 0) - (b.position ?? 0),
  );
  const groups = groupStepsByContiguousGroupTitle(ordered);
  const ids: string[] = [];

  for (const group of groups) {
    const head = group.items[0];
    const meta = parseBlockGroupMeta(head?.groupDescription);
    const summaryLine = meta.workoutType
      ? buildCustomWorkoutSummaryLine(meta.workoutType, meta.workoutParams ?? {})
      : null;
    const completable = selectCompletableStepsInBlock(
      group.items,
      summaryLine,
      meta.instruction,
    );
    if (completable.length === 0 || !completable.every(isStepCompleted)) {
      continue;
    }
    const completableIds = new Set(completable.map((step) => step.id));
    for (const step of group.items) {
      if (
        !completableIds.has(step.id) &&
        (step.type ?? "CHECKBOX") === "CHECKBOX" &&
        !step.isDone
      ) {
        ids.push(step.id);
      }
    }
  }

  return ids;
};
